<template>
  <div class="episode-slots">
    <!-- Completeness banner (top) -->
    <div class="slot-status" :class="completeness.isComplete ? 'is-ready' : 'is-incomplete'">
      <font-awesome-icon :icon="completeness.isComplete ? ['fas', 'circle-check'] : ['fas', 'triangle-exclamation']" class="mr-2" />
      <span v-if="completeness.isComplete">All slots approved — ready for client review</span>
      <span v-else>{{ needsSummary }} before client review</span>
    </div>

    <!-- Grouped like the client rail: still needed on top, done below. Within a
         group the order is Full Episode → Title & Show Notes → Thumbnail → Social cuts -->
    <template v-for="entry in groupedSlots" :key="entry.key">
      <p v-if="entry.type === 'heading'" class="slot-group">{{ entry.label }}</p>
      <!-- Video item slot -->
      <div
        v-else-if="entry.type === 'item'"
        class="slot-card"
        :class="[slotStateClass(entry.key), { 'is-clickable': !!itemFor(entry.slot.key), 'is-active': isActive(entry.slot.key) }]"
        @click="itemFor(entry.slot.key) && select(entry.slot.key)"
      >
        <div class="slot-head">
          <span class="slot-label">{{ entry.slot.label }}</span>
          <span v-if="entry.key === 'thumbnail' && clientProvidesThumbnail" class="tag is-small" :class="hasClientThumbnail ? 'is-success' : 'is-light-empty'">
            {{ hasClientThumbnail ? 'Client provided' : 'Awaiting client' }}
          </span>
          <span v-else-if="itemFor(entry.slot.key)" class="slot-tags">
            <span class="slot-tag">
              <span class="tag-label">Internal</span>
              <span class="tag is-small" :class="statusClass(itemFor(entry.slot.key).internalStatus)">{{ statusLabel(itemFor(entry.slot.key).internalStatus) }}</span>
            </span>
            <span class="slot-tag">
              <span class="tag-label">Client</span>
              <span class="tag is-small" :class="clientTagClass(itemFor(entry.slot.key))">{{ clientTagLabel(itemFor(entry.slot.key)) }}</span>
            </span>
          </span>
          <span v-else-if="finalAssetFor(entry.slot.key)" class="slot-tags">
            <span class="slot-tag"><span class="tag-label">Internal</span><span class="tag is-small is-success">Approved</span></span>
            <span class="slot-tag"><span class="tag-label">Client</span><span class="tag is-small is-success">Approved</span></span>
          </span>
          <span v-else class="tag is-small is-light-empty">Empty</span>
        </div>

        <!-- Client-provided thumbnail: read-only, no admin upload -->
        <template v-if="entry.key === 'thumbnail' && clientProvidesThumbnail">
          <div v-if="hasClientThumbnail" class="slot-body">
            <figure class="slot-thumb">
              <BunnyImage :path="project.episodeThumbnailPath" :fallback="project.episodeThumbnailUrl" :alt="entry.slot.label" />
            </figure>
            <span class="slot-name">Provided by client</span>
          </div>
          <div v-else class="slot-drop is-static">
            <span class="icon has-text-grey"><font-awesome-icon :icon="['fal', 'user-clock']" /></span>
            <span class="is-size-7 has-text-grey">Client will provide thumbnail</span>
          </div>
        </template>

        <!-- Filled: thumbnail + select + replace -->
        <template v-else-if="itemFor(entry.slot.key)">
          <div class="slot-body">
            <figure class="slot-thumb">
              <img v-if="itemFor(entry.slot.key).poster" :src="itemFor(entry.slot.key).poster" :alt="entry.slot.label" />
              <span v-else class="icon has-text-grey-light"><font-awesome-icon :icon="['fal', 'film']" /></span>
            </figure>
            <span class="slot-name">{{ itemFor(entry.slot.key).name }}</span>
          </div>
          <div class="slot-actions">
            <div v-if="busy[entry.slot.key]" class="slot-uploading">
              <CustomProgressBar :value="progress[entry.slot.key] || 0" :total="100" theme="success" :rightText="`${progress[entry.slot.key] || 0}%`" />
            </div>
            <template v-else>
              <button v-if="itemFor(entry.slot.key).status !== REVIEW_ITEM_STATUS.APPROVED" class="button is-text is-small" title="Replace the latest version's file. Keeps comments." @click.stop="emitFor('replace', entry.slot.key)">
                Replace
              </button>
              <button class="button is-text is-small" @click.stop="emitFor('rename', entry.slot.key)">Rename</button>
              <button class="button is-text is-small" title="Download original" @click.stop="emitFor('download', entry.slot.key)">Download</button>
              <button v-if="itemFor(entry.slot.key).status !== REVIEW_ITEM_STATUS.APPROVED" class="button is-text is-small has-text-danger" @click.stop="emitFor('remove', entry.slot.key)">
                <font-awesome-icon :icon="['fal', 'trash-can']" class="mr-1" />Delete
              </button>
            </template>
          </div>
        </template>

        <!-- Client approved: the review item was promoted to the library and deleted. Read-only. -->
        <template v-else-if="finalAssetFor(entry.slot.key) && !busy[entry.slot.key]">
          <div class="slot-body">
            <figure class="slot-thumb">
              <BunnyImage :asset-id="finalAssetFor(entry.slot.key).id" :fallback="finalAssetFor(entry.slot.key).bunnyStorage?.thumbnailUrl || finalAssetFor(entry.slot.key).bunnyStream?.thumbnailUrl" :alt="entry.slot.label" />
            </figure>
            <span class="slot-name">{{ finalAssetFor(entry.slot.key).name }}</span>
          </div>
        </template>

        <!-- Uploading: filling progress bar -->
        <div v-else-if="busy[entry.slot.key]" class="slot-uploading">
          <CustomProgressBar :value="progress[entry.slot.key] || 0" :total="100" theme="success" :rightText="`${progress[entry.slot.key] || 0}%`" />
        </div>
        <!-- Empty: dropzone -->
        <label v-else class="slot-drop">
          <input class="is-hidden" type="file" :accept="entry.slot.mediaType === 'image' ? 'image/*' : 'video/*'" @change="onFill(entry.slot, $event)" />
          <span class="icon has-text-grey"><font-awesome-icon :icon="['fal', entry.slot.mediaType === 'image' ? 'image' : 'arrow-up-from-bracket']" /></span>
          <span class="is-size-7 has-text-grey">Upload {{ entry.slot.label }}</span>
        </label>
      </div>

      <!-- Optional extra asset: always last, adds one more item slot -->
      <div v-else-if="entry.type === 'add'" class="slot-card slot-add">
        <div v-if="busy.__add__" class="slot-uploading">
          <CustomProgressBar :value="progress.__add__ || 0" :total="100" theme="success" :rightText="`${progress.__add__ || 0}%`" />
        </div>
        <label v-else class="slot-drop">
          <input class="is-hidden" type="file" accept="video/*,image/*" @change="onAddExtra" />
          <span class="icon has-text-grey"><font-awesome-icon :icon="['fal', 'plus']" /></span>
          <span class="is-size-7 has-text-grey">Add another asset</span>
        </label>
      </div>

      <!-- Title & Show Notes — opens the authoring area in the main panel -->
      <div v-else-if="entry.type === 'meta'" class="slot-card slot-nav" :class="[{ 'is-active': metaActive }, completeness.filled.episode_meta ? 'is-done' : 'is-needed']" @click="$emit('select-meta')">
        <div class="slot-head">
          <span class="slot-label">Title &amp; Show Notes</span>
          <span class="tag is-small" :class="completeness.filled.episode_meta ? 'is-success' : 'is-light-empty'">
            {{ completeness.filled.episode_meta ? 'Set' : 'Empty' }}
          </span>
        </div>
        <div class="slot-nav-body">
          <span class="icon has-text-grey"><font-awesome-icon :icon="['fal', 'pen-line']" /></span>
          <span class="is-size-7 has-text-grey">{{ project.episodeTitle || 'Add title & show notes' }}</span>
        </div>
      </div>

    </template>
  </div>
</template>

<script setup>
import { reactive, computed } from 'vue'
import { useAdminReviewStore } from '@/stores/adminReviewStore'
import { logger } from '@/services/loggerService'
import { toastBridge as toast } from '@/services/toastBridge'
import CustomProgressBar from '@/components/common/CustomProgressBar.vue'
import BunnyImage from '@/components/common/BunnyImage.vue'
import {
  PODCAST_REVIEW_SLOTS,
  EXTRA_SLOT_PREFIX,
  isExtraSlot,
  getEpisodeSlotCompleteness,
  REVIEW_ITEM_STATUS,
} from '@/utils/reviewUtils'

const props = defineProps({
  deliverable: { type: Object, required: true },
  project: { type: Object, required: true }, // the episode project doc
  items: { type: Array, default: () => [] }, // reviewItems (carry slot, poster, status)
  activeIndex: { type: Number, default: 0 },
  companyId: { type: String, default: null },
  metaActive: { type: Boolean, default: false }, // is the Title & Show Notes view open
  finalAssets: { type: Array, default: () => [] }, // promoted library assets for this deliverable
})

const emit = defineEmits(['select', 'select-meta', 'remove', 'replace', 'rename', 'download'])

const store = useAdminReviewStore()

const itemSlots = PODCAST_REVIEW_SLOTS.filter((s) => s.target === 'item')
const slotByKey = (k) => itemSlots.find((s) => s.key === k)

// Title & Show Notes only applies when Anthem distributes the show (show setting,
// copied onto the deliverable). Computed, not a constant, because the deliverable
// can change under the component.
const anthemDistributes = computed(() => props.deliverable?.anthemDistributes !== false)

// Render order: Full Episode → Title & Show Notes → Thumbnail → Social cuts.
const orderedSlots = computed(() => [
  { type: 'item', key: 'full_episode', slot: slotByKey('full_episode') },
  ...(anthemDistributes.value ? [{ type: 'meta', key: 'meta' }] : []),
  { type: 'item', key: 'thumbnail', slot: slotByKey('thumbnail') },
  ...itemSlots
    .filter((s) => s.key !== 'full_episode' && s.key !== 'thumbnail')
    .map((s) => ({ type: 'item', key: s.key, slot: s })),
  ...extraSlots.value.map((s) => ({ type: 'item', key: s.key, slot: s })),
])

// Extras live on the review item until client approval promotes it (reviewSlot is
// stamped on the library copy), so read both to keep an approved extra in the list.
const extraSlots = computed(() => {
  const seen = new Map()
  for (const it of props.items) {
    if (isExtraSlot(it.slot)) seen.set(it.slot, { key: it.slot, label: it.name || 'Extra asset', mediaType: it.type === 'image' ? 'image' : 'video' })
  }
  for (const a of props.finalAssets) {
    if (isExtraSlot(a.reviewSlot) && !seen.has(a.reviewSlot)) {
      seen.set(a.reviewSlot, { key: a.reviewSlot, label: a.name || 'Extra asset', mediaType: 'video' })
    }
  }
  return [...seen.values()]
})

// Per-slot upload progress/busy, keyed by slot key.
const busy = reactive({})
const progress = reactive({})

function itemFor(slotKey) {
  return props.items.find((i) => i.slot === slotKey) || null
}
// Client approval promotes the item to the library (stamped with reviewSlot) and
// deletes the review item — so an empty slot with a matching asset is DONE, not missing.
function finalAssetFor(slotKey) {
  const stamped = props.finalAssets.find((a) => a.reviewSlot === slotKey)
  if (stamped) return stamped
  // Assets promoted before reviewSlot was stamped carry no slot. The thumbnail is
  // the only image slot, so a review-approved image can only have come from it.
  if (slotKey === 'thumbnail') {
    return props.finalAssets.find((a) => !a.reviewSlot && a.uploadSource === 'review-approval' && String(a.type || '').startsWith('image/')) || null
  }
  return null
}
function isActive(slotKey) {
  const item = itemFor(slotKey)
  return item && props.items[props.activeIndex]?.id === item.id
}
function select(slotKey) {
  const idx = props.items.findIndex((i) => i.slot === slotKey)
  if (idx >= 0) emit('select', idx)
}
function emitFor(event, slotKey) {
  const idx = props.items.findIndex((i) => i.slot === slotKey)
  if (idx >= 0) emit(event, idx)
}

// When ON, the client supplies the thumbnail (project doc, no admin upload / no
// review item). The slot then counts as done via project.episodeThumbnailPath/Url.
const clientProvidesThumbnail = computed(() => !!props.deliverable?.clientProvidesThumbnail)
const hasClientThumbnail = computed(() => !!(props.project?.episodeThumbnailPath || props.project?.episodeThumbnailUrl))

// requireApproved so the rail agrees with the actual client-review gate
// (server: checkEpisodeSlotsMissing) — an uploaded-but-unapproved item still blocks.
const completeness = computed(() =>
  getEpisodeSlotCompleteness(props.items, props.project, {
    requireApproved: true,
    clientProvidesThumbnail: clientProvidesThumbnail.value,
    anthemDistributes: anthemDistributes.value,
  }),
)

// "2 uploads · 3 approvals" — split so an all-uploaded episode doesn't read as "5 slots needed".
const needsSummary = computed(() => {
  const missing = completeness.value.missing
  const uploads = missing.filter((k) => k !== 'episode_meta' && !itemFor(k)).length
  const meta = missing.includes('episode_meta') ? 1 : 0
  const approvals = missing.length - uploads - meta
  const parts = []
  if (uploads) parts.push(`${uploads} upload${uploads === 1 ? '' : 's'}`)
  if (meta) parts.push('title & show notes')
  if (approvals) parts.push(`${approvals} internal approval${approvals === 1 ? '' : 's'}`)
  return parts.join(' · ') + ' needed'
})

// Split into bands: anything still blocking client review first, done below.
const groupedSlots = computed(() => {
  const isDone = (e) => (e.type === 'meta' ? completeness.value.filled.episode_meta : slotStateClass(e.key) === 'is-done')
  const needed = orderedSlots.value.filter((e) => !isDone(e))
  const done = orderedSlots.value.filter(isDone)
  const out = []
  // The add control isn't a slot — it sits after both bands and never counts.
  const add = { type: 'add', key: '__add__' }
  if (needed.length) out.push({ type: 'heading', key: '__needed__', label: `Still needed (${needed.length})` }, ...needed)
  if (done.length) out.push({ type: 'heading', key: '__done__', label: `Complete (${done.length})` }, ...done)
  out.push(add)
  return out
})

// Left-edge accent: needed (empty) → amber, awaiting approval → blue, done → green.
function slotStateClass(slotKey) {
  if (slotKey === 'thumbnail' && clientProvidesThumbnail.value) return hasClientThumbnail.value ? 'is-done' : 'is-needed'
  const it = itemFor(slotKey)
  if (!it) return finalAssetFor(slotKey) ? 'is-done' : 'is-needed'
  return it.status === REVIEW_ITEM_STATUS.APPROVED ? 'is-done' : 'is-pending'
}

// ── Item slots (videos + the thumbnail image) ───────────────
async function onFill(slot, event) {
  const file = event.target.files?.[0]
  event.target.value = ''
  await uploadToSlot(slot, file)
}

function onAddExtra(event) {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file) return
  const mediaType = file.type.startsWith('image/') ? 'image' : 'video'
  uploadToSlot({ key: `${EXTRA_SLOT_PREFIX}${Date.now()}`, label: file.name, mediaType }, file, '__add__')
}

async function uploadToSlot(slot, file, busyKey = slot.key) {
  if (!file || !props.companyId) return
  busy[busyKey] = true
  progress[busyKey] = 0
  try {
    // addItem needs the review to exist; auto-create on first slot fill.
    if (!store.review) {
      await store.createReview(props.deliverable.id, props.project.id, props.companyId)
    }
    await store.addItem(
      { name: file.name.replace(/\.[^/.]+$/, ''), type: slot.mediaType, slot: slot.key },
      file,
      props.companyId,
      (p) => { progress[busyKey] = Math.round(p) }
    )
  } catch (err) {
    logger.error('[EpisodeReviewSlots] Slot upload failed', { slot: slot.key, error: err.message })
    toast.error(`Failed to upload ${slot.label}`)
  } finally {
    busy[busyKey] = false
  }
}

// The thumbnail slot doubles as the episode's display thumbnail; the store
// wrappers (adminReviewStore.syncEpisodeThumbnail) keep project.episodeThumbnailPath
// on the newest version — no library asset is created until client approval.

// ── Status badges ────────────────────────────────────────────
function statusLabel(status) {
  if (status === REVIEW_ITEM_STATUS.APPROVED) return 'Approved'
  if (status === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) return 'Changes'
  if (status === REVIEW_ITEM_STATUS.IN_REVISION) return 'In revision'
  return 'Pending'
}
function statusClass(status) {
  if (status === REVIEW_ITEM_STATUS.APPROVED) return 'is-success'
  if (status === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) return 'is-warning'
  return 'is-info'
}
// Client side only starts once a version has been promoted; before that it's not
// "pending" on the client, it simply hasn't reached them.
function clientDelivered(item) {
  return (item.versions || []).some((v) => v?.clientVersionNumber != null)
}
function clientTagLabel(item) {
  if (!clientDelivered(item)) return 'Not sent'
  if (item.clientStatus === REVIEW_ITEM_STATUS.PENDING_FINALIZATION) return 'Finalizing'
  return statusLabel(item.clientStatus)
}
function clientTagClass(item) {
  if (!clientDelivered(item)) return 'is-light-empty'
  return statusClass(item.clientStatus)
}
</script>

<style lang="scss" scoped>
.episode-slots {
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

// Mirrors .erp-rail-group on the client rail.
.slot-group {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin: 0.25rem 0 -0.25rem;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: hsl(0, 0%, 55%);
  &::after { content: ''; flex: 1 1 auto; height: 1px; background: hsl(0, 0%, 88%); }
}

.slot-card {
  border: 1px solid hsl(0, 0%, 90%);
  border-left: 3px solid hsl(0, 0%, 85%);
  border-radius: 6px;
  padding: 0.5rem 0.65rem;
  &.is-needed { border-left-color: hsl(45, 90%, 55%); }
  &.is-pending { border-left-color: hsl(207, 70%, 60%); }
  &.is-done { border-left-color: hsl(158, 60%, 45%); }
  &.is-clickable { cursor: pointer; transition: border-color 0.15s ease, background 0.15s ease; }
  &.is-clickable:hover { border-color: var(--bulma-primary, hsl(217, 71%, 53%)); }
  &.is-active { background: var(--bulma-primary-light, hsl(217, 71%, 95%)); border-color: var(--bulma-primary, hsl(217, 71%, 53%)); }
}

.slot-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.4rem;
}
.slot-label { font-size: 0.8rem; font-weight: 600; }
.tag.is-light-empty { background: hsl(0, 0%, 94%); color: hsl(0, 0%, 55%); }
.slot-tags { display: flex; gap: 0.35rem; }
.slot-tag { display: flex; flex-direction: column; align-items: center; gap: 0.1rem; }
.tag-label { font-size: 0.58rem; line-height: 1; text-transform: uppercase; letter-spacing: 0.05em; color: hsl(0, 0%, 65%); }

.slot-body {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 4px;
  padding: 0.25rem;
}
.slot-thumb {
  width: 48px;
  height: 32px;
  border-radius: 3px;
  overflow: hidden;
  background: hsl(0, 0%, 93%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.slot-name { font-size: 0.78rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.slot-actions { margin-top: 0.25rem; text-align: right; }

.slot-thumb-wide {
  img { width: 100%; border-radius: 4px; object-fit: cover; aspect-ratio: 16 / 9; }
}

.slot-drop {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: 1px dashed hsl(0, 0%, 80%);
  border-radius: 4px;
  padding: 0.75rem;
  cursor: pointer;
  transition: border-color 0.15s ease;
  &:hover { border-color: var(--bulma-primary, hsl(217, 71%, 53%)); }
}

.slot-uploading {
  width: 100%;
  margin-top: 0.25rem;
}

.slot-nav {
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  &:hover { border-color: var(--bulma-primary, hsl(217, 71%, 53%)); }
  &.is-active { background: var(--bulma-primary-light, hsl(217, 71%, 95%)); border-color: var(--bulma-primary, hsl(217, 71%, 53%)); }
}
.slot-nav-body {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  overflow: hidden;
  span.is-size-7 { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
}

.slot-divider { margin: 0.25rem 0; background: hsl(0, 0%, 90%); }

.slot-status {
  display: flex;
  align-items: center;
  font-size: 0.78rem;
  padding: 0.5rem 0.65rem;
  border-radius: 6px;
  &.is-ready { background: hsl(158, 60%, 94%); color: hsl(158, 60%, 30%); }
  &.is-incomplete { background: hsl(45, 90%, 93%); color: hsl(35, 70%, 38%); }
}
</style>
